"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.serverUserEndpointInfo = exports.serverRoleNames = exports.projectRoleNames = exports.projectRoleEndpointMap = exports.explicitGrantOnlyEndpoints = exports.reportReadOnlyEndpoints = exports.serverUserEndpoints = exports.serverUserEndpointsConst = exports.endpointCategories = void 0;
exports.getEndpointName = getEndpointName;
exports.getProjectRolesForEndpoint = getProjectRolesForEndpoint;
exports.projectAclDeniedMessage = projectAclDeniedMessage;
exports.globalRoleDeniedMessage = globalRoleDeniedMessage;
const dbschema_1 = require("@karya/dbschema");
// Endpoint categories for documentation and grouping
exports.endpointCategories = {
    PROJECT_ACCESS: [
        'ACCESS_PROJECT',
        'ASSIGN_USER_TO_PROJECT',
        'GET_USER_PROJECT_ACL',
        'GET_USER_ACLS',
        'GET_PROJECT_USERS',
        'GET_PROJECT_ORG_CONFIG',
        'UPDATE_PROJECT_PREBATCHING',
    ],
    REPORTS: [
        'GET_REPORT_KINDS',
        'GET_REPORTS',
        'GET_REPORTS_BY_KIND',
        'GET_REPORT_BY_ID',
        'GET_REPORT_RESULT',
        'GET_REPORT_RESULT_DOWNLOAD_URL',
        'GET_REPORT',
        'GET_REPORT_MOBILE',
        'GET_REPORT_RESULT_MOBILE',
        'GET_REPORT_BY_ID_MOBILE',
        'GET_REPORT_RESULT_DOWNLOAD_URL_MOBILE',
        'CREATE_REPORT',
        'UPDATE_REPORT',
        'DELETE_REPORT',
    ],
    DATASETS: [
        'CREATE_DATASET',
        'GET_ALL_DATASETS',
        'GET_DATASET',
        'INGEST_DATAPOINTS',
        'INGEST_DATAPOINT',
        'GET_DATAPOINTS',
        'CREATE_SUBSCRIPTION',
        'GET_SUBSCRIPTIONS',
        'UPDATE_SUBSCRIPTION_STATUS',
        'GET_DATASET_OPS',
        'CREATE_EXPORT',
        'START_DIRECT_UPLOAD_IMPORT',
        'REPORT_DIRECT_UPLOAD_FILES',
    ],
    TASKS: [
        'CREATE_TASK',
        'EDIT_TASK',
        'GET_TASKS',
        'GET_TASK_DRAFT',
        'SAVE_TASK_DRAFT',
        'GET_TASK',
        'QR_CODE_PAYLOAD',
        'MARK_TASK_COMPLETE',
        'MOVE_TASK_TO_LIVE',
        'CREATE_TASK_ASSIGNMENT',
        'GET_TASK_ASSIGNMENTS',
        'EDIT_TASK_ASSIGNMENT',
        'GET_ASSIGNABLE_MICROTASK_COUNT',
        'UPDATE_TASK_PREBATCHING',
        'GET_TASK_PREBATCH_STATUS',
        'GET_TASK_HEALTH',
    ],
    AVATARS: [
        'GENERATE_AVATARS',
        'LINK_AVATARS_BY_PHONE',
        'GET_AUTO_LINK_AVATARS',
        'LINK_AVATARS_WITH_WORKER_ID',
        'GET_AUTO_LINK_AVATARS_WITH_WORKER_ID',
        'GET_AVATAR_GEN_BULKS',
        'GET_AVATARS_FOR_BULK',
        'GET_AVATAR_SUMMARY',
        'SET_AVATAR_STATUS',
        'GET_WORKER_SUMMARY_FOR_TASK',
        'GET_AVATAR_WORKER_SUMMARY_FOR_TASK',
        'LINK_AVATAR',
    ],
    PAYMENTS: [
        'GET_ELIGIBLE_WORKERS_FOR_PAYMENT',
        'INITIATE_BULK_TRANSACTION',
        'GET_ALL_BULK_TRANSACTIONS',
        'GET_BULK_TRANSACTION_SUMMARIES',
        'GET_ALL_TRANSACTIONS',
        'GET_ALL_ACCOUNTS',
        'GET_WORKER_ACCOUNTS',
        'GET_WORKER_TASK_LEDGER',
        'GET_WORKER_PAYMENT_LEDGER',
        'GET_ADDITIONAL_PAYMENT_LEDGER',
        'GET_TASK_PAYMENT_ROWS',
    ],
    WORKER_PII: ['GET_WORKER_MASKED_PII', 'REVEAL_WORKER_PII_FIELD'],
    SCRIPTS: ['GET_SCRIPTS', 'EXECUTE_SCRIPT', 'GET_ALL_SCRIPT_RUNS', 'GET_SCRIPT_RUN_DETAILS'],
    CONFERENCER: ['GET_CONFERENCER_SUMMARY', 'GET_CONFERENCER_RECORDS', 'GET_CONFERENCER_RECORDS_SUMMARY'],
    TROUBLESHOOTING: ['GET_TROUBLESHOOTING_WORKER_LOOKUP', 'GET_TROUBLESHOOTING_WORKER'],
    PLATFORM: ['UPDATE_LANGUAGE_ASSET'],
    PROJECT_LIFECYCLE: ['MARK_PROJECT_COMPLETED', 'MARK_PROJECT_INACTIVE', 'MARK_PROJECT_ACTIVE'],
    // Finance-only KYC access. Deliberately its own category rather than part of WORKER_PII: the
    // export decrypts document numbers, which is a strictly higher privilege than revealing a
    // masked field, and must not be granted by widening an existing category.
    KYC_FINANCE: ['GET_KYC_VERIFIED_WORKERS', 'DOWNLOAD_KYC_PAN_REPORT'],
};
// List of server user endpoints (derived from categories, order preserved)
exports.serverUserEndpointsConst = [
    ...exports.endpointCategories.PROJECT_ACCESS,
    ...exports.endpointCategories.REPORTS,
    ...exports.endpointCategories.DATASETS,
    ...exports.endpointCategories.TASKS,
    ...exports.endpointCategories.AVATARS,
    ...exports.endpointCategories.PAYMENTS,
    ...exports.endpointCategories.WORKER_PII,
    ...exports.endpointCategories.SCRIPTS,
    ...exports.endpointCategories.CONFERENCER,
    ...exports.endpointCategories.TROUBLESHOOTING,
    ...exports.endpointCategories.PLATFORM,
    ...exports.endpointCategories.PROJECT_LIFECYCLE,
    ...exports.endpointCategories.KYC_FINANCE,
];
exports.serverUserEndpoints = exports.serverUserEndpointsConst.slice();
// Read-only report endpoints (the REPORTS category minus create/update/delete).
// Shared by roles that may view but not manage reports.
exports.reportReadOnlyEndpoints = [
    'GET_REPORT_KINDS',
    'GET_REPORTS',
    'GET_REPORTS_BY_KIND',
    'GET_REPORT_BY_ID',
    'GET_REPORT_RESULT',
    'GET_REPORT_RESULT_DOWNLOAD_URL',
    'GET_REPORT',
    'GET_REPORT_MOBILE',
    'GET_REPORT_RESULT_MOBILE',
    'GET_REPORT_BY_ID_MOBILE',
    'GET_REPORT_RESULT_DOWNLOAD_URL_MOBILE',
];
/**
 * Endpoints that must never be inherited implicitly by a role defined as "every endpoint".
 *
 * PROJECT_MANAGER is built by spreading `serverUserEndpointsConst`, so anything added to any
 * category is granted to it automatically. The KYC finance export returns DECRYPTED document
 * numbers, which is a higher privilege than anything else in the model, so it is granted only where
 * it is listed by name — today that is FINANCE_MANAGER alone.
 *
 * Add an endpoint here whenever "the most privileged project role gets it for free" is the wrong
 * default.
 */
exports.explicitGrantOnlyEndpoints = [...exports.endpointCategories.KYC_FINANCE];
// Mapping from project role to endpoints (used for user_project_acl authorization)
exports.projectRoleEndpointMap = {
    // PROJECT_MANAGER: every endpoint EXCEPT the explicit-grant-only ones (see
    // explicitGrantOnlyEndpoints) — notably the KYC finance PAN export, which must not be inherited.
    PROJECT_MANAGER: exports.serverUserEndpointsConst.filter((endpoint) => !exports.explicitGrantOnlyEndpoints.includes(endpoint)),
    // PROJECT_COORDINATOR: PROJECT_ACCESS (no ASSIGN), REPORTS read-only, DATASETS get-only, TASKS get-only, AVATARS all, PAYMENTS get-only, SCRIPTS all, CONFERENCER all
    PROJECT_COORDINATOR: [
        'ACCESS_PROJECT',
        'GET_USER_PROJECT_ACL',
        'GET_USER_ACLS',
        'GET_PROJECT_ORG_CONFIG',
        ...exports.reportReadOnlyEndpoints,
        'GET_ALL_DATASETS',
        'GET_DATASET',
        'GET_DATAPOINTS',
        'GET_SUBSCRIPTIONS',
        'GET_DATASET_OPS',
        'GET_TASKS',
        'GET_TASK',
        'QR_CODE_PAYLOAD',
        'GET_TASK_ASSIGNMENTS',
        'GET_ASSIGNABLE_MICROTASK_COUNT',
        'GET_TASK_PREBATCH_STATUS',
        'GET_TASK_HEALTH',
        ...exports.endpointCategories.AVATARS,
        'GET_ELIGIBLE_WORKERS_FOR_PAYMENT',
        'GET_ALL_BULK_TRANSACTIONS',
        'GET_ALL_TRANSACTIONS',
        'GET_ALL_ACCOUNTS',
        'GET_WORKER_ACCOUNTS',
        'GET_WORKER_TASK_LEDGER',
        'GET_WORKER_PAYMENT_LEDGER',
        'GET_ADDITIONAL_PAYMENT_LEDGER',
        'GET_TASK_PAYMENT_ROWS',
        ...exports.endpointCategories.SCRIPTS,
        ...exports.endpointCategories.CONFERENCER,
    ],
    // FINANCE_MANAGER: PROJECT_ACCESS (no ASSIGN), REPORTS read-only, PAYMENTS all, SCRIPTS all,
    // KYC_FINANCE all — the only role permitted to export decrypted document numbers
    FINANCE_MANAGER: [
        'ACCESS_PROJECT',
        'GET_USER_PROJECT_ACL',
        'GET_USER_ACLS',
        'GET_PROJECT_ORG_CONFIG',
        ...exports.reportReadOnlyEndpoints,
        ...exports.endpointCategories.PAYMENTS,
        ...exports.endpointCategories.SCRIPTS,
        ...exports.endpointCategories.KYC_FINANCE,
    ],
    // TECH_SUPPORT: PROJECT_ACCESS (no ASSIGN), REPORTS all, DATASETS all, TASKS all, AVATARS all, SCRIPTS all, CONFERENCER all
    TECH_SUPPORT: [
        'ACCESS_PROJECT',
        'GET_USER_PROJECT_ACL',
        'GET_USER_ACLS',
        'GET_PROJECT_ORG_CONFIG',
        ...exports.endpointCategories.REPORTS,
        ...exports.endpointCategories.DATASETS,
        ...exports.endpointCategories.TASKS,
        ...exports.endpointCategories.AVATARS,
        ...exports.endpointCategories.SCRIPTS,
        ...exports.endpointCategories.CONFERENCER,
    ],
    // EXTERNAL_COORDINATOR: read-only access to reports in a project (no create/update/delete, no CSV download)
    EXTERNAL_COORDINATOR: [
        'ACCESS_PROJECT',
        'GET_USER_PROJECT_ACL',
        'GET_PROJECT_ORG_CONFIG',
        'GET_REPORT_KINDS',
        'GET_REPORTS',
        'GET_REPORTS_BY_KIND',
        'GET_REPORT_BY_ID',
        'GET_REPORT_RESULT',
        'GET_REPORT',
        'GET_REPORT_MOBILE',
        'GET_REPORT_RESULT_MOBILE',
        'GET_REPORT_BY_ID_MOBILE',
    ],
};
// Project role names (for ACL assignment UI)
exports.projectRoleNames = {
    PROJECT_MANAGER: 'Project Manager',
    PROJECT_COORDINATOR: 'Project Coordinator',
    FINANCE_MANAGER: 'Finance Manager',
    TECH_SUPPORT: 'Tech Support',
    EXTERNAL_COORDINATOR: 'External Coordinator',
};
// Global role names (for user-facing authorization messages)
exports.serverRoleNames = {
    SUPER_ADMIN: 'Super Admin',
    ADMIN: 'Administrator',
    COORDINATOR: 'Coordinator',
    EXTERNAL_COORDINATOR: 'External Coordinator',
    INACTIVE: 'Inactive',
};
/**
 * Resolve a human-readable display name for a server user endpoint / action.
 *
 * `ASSIGN_USER_TO_PROJECT` is intentionally excluded from `serverUserEndpointInfo`,
 * so it is special-cased here. For every other endpoint the name is read from
 * `serverUserEndpointInfo`, falling back to the raw endpoint key if no entry exists.
 * @param endpoint The endpoint constant to label.
 * @returns The endpoint's friendly name, or the raw endpoint string as a fallback.
 * @example getEndpointName('CREATE_TASK') // => 'Create Task'
 * @example getEndpointName('ASSIGN_USER_TO_PROJECT') // => 'Assign User to Project'
 */
function getEndpointName(endpoint) {
    var _a, _b;
    if (endpoint === 'ASSIGN_USER_TO_PROJECT') {
        return 'Assign User to Project';
    }
    return (_b = (_a = exports.serverUserEndpointInfo[endpoint]) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : endpoint;
}
/**
 * List every `ProjectRole` permitted to access the given `ServerUserEndpoint`.
 *
 * Allowed roles are derived from `projectRoleEndpointMap` (a role is included
 * when its endpoint list contains `endpoint`).
 * @param endpoint The endpoint to look up.
 * @returns The project roles that can access `endpoint`, in declaration order.
 * @example getProjectRolesForEndpoint('CREATE_TASK') // => ['PROJECT_MANAGER', 'TECH_SUPPORT']
 */
function getProjectRolesForEndpoint(endpoint) {
    return dbschema_1.projectRoles.filter((role) => exports.projectRoleEndpointMap[role].includes(endpoint));
}
/**
 * Build a user-facing message explaining why a project-scoped action was denied.
 * Used by the backend authorization middleware to return a meaningful 403 instead
 * of a generic "not allowed to access this endpoint" string.
 */
function projectAclDeniedMessage(args) {
    const action = getEndpointName(args.endpoint);
    // No project role assigned: the user has never been added to this project.
    if (!args.projectRole) {
        return `You don't have a role on this project, so you can't perform this action (${action}). Ask a project manager to grant you access.`;
    }
    const allowedRoles = getProjectRolesForEndpoint(args.endpoint).map((role) => exports.projectRoleNames[role]);
    const allowedList = allowedRoles.length > 0 ? allowedRoles.join(', ') : 'no project roles';
    return `Your project role "${exports.projectRoleNames[args.projectRole]}" can't perform this action (${action}). This action is available to: ${allowedList}.`;
}
/**
 * Build a user-facing message explaining why a global-role-gated action was denied
 * (e.g. creating a project requires an Administrator). `action` is a human-readable
 * label for the attempted operation.
 */
function globalRoleDeniedMessage(args) {
    const allowedList = args.allowedRoles.map((role) => exports.serverRoleNames[role]).join(', ');
    return `You don't have permission to perform this action (${args.action}). It requires one of these roles: ${allowedList}. Your current role is ${exports.serverRoleNames[args.currentRole]}.`;
}
// Server user endpoint names and description
exports.serverUserEndpointInfo = {
    ACCESS_PROJECT: {
        name: 'Access Projects',
        description: 'Get basic access to project details',
    },
    CREATE_DATASET: {
        name: 'Create Datasets',
        description: 'Allows user to create new datasets',
    },
    GET_USER_PROJECT_ACL: {
        name: 'Get User ACL',
        description: 'Get the access control for the current user on a project',
    },
    GET_USER_ACLS: {
        name: 'Get Project Users',
        description: 'Get the access control for all the users associated with a project',
    },
    GET_PROJECT_USERS: {
        name: 'Get Assignable Users',
        description: 'Get users who can be added to the project (for add-user form)',
    },
    GET_PROJECT_ORG_CONFIG: {
        name: 'Get Project Org Config',
        description: 'Get org config (and payment config if present) for the current project',
    },
    GET_REPORT_KINDS: {
        name: 'Get Report Kinds',
        description: 'Get all report kinds',
    },
    GET_REPORTS: {
        name: 'Get All Reports',
        description: 'Get all reports',
    },
    GET_REPORTS_BY_KIND: {
        name: 'Get Reports By Kind',
        description: 'Get reports filtered by kind',
    },
    GET_REPORT_BY_ID: {
        name: 'Get Report By Id',
        description: 'Get a specific report by id',
    },
    GET_REPORT: {
        name: 'Get Report',
        description: 'Get a report by id',
    },
    GET_REPORT_MOBILE: {
        name: 'Get Report (Mobile)',
        description: 'Get a report for mobile',
    },
    GET_REPORT_RESULT: {
        name: 'Get Report Result',
        description: 'Generate report result',
    },
    GET_ALL_DATASETS: {
        name: 'Get Datasets',
        description: 'Get all datasets in the project',
    },
    GET_DATASET: {
        name: 'Get Dataset',
        description: 'Get details of a specific dataset',
    },
    INGEST_DATAPOINTS: {
        name: 'Add datapoints',
        description: 'Add new datapoints to a dataset',
    },
    INGEST_DATAPOINT: {
        name: 'Add datapoint',
        description: 'Add a new datapoint to a dataset',
    },
    GET_DATAPOINTS: {
        name: 'Get Datapoints',
        description: 'Get datapoints of a dataset',
    },
    CREATE_SUBSCRIPTION: {
        name: 'Create Dataset Subscription',
        description: 'Subscribe tasks to datasets',
    },
    GET_SUBSCRIPTIONS: {
        name: 'Get Subscriptions',
        description: 'Get all subscriptions for tasks of the project',
    },
    UPDATE_SUBSCRIPTION_STATUS: {
        name: 'Update Subscription Status',
        description: 'Activate or deactivate a dataset subscription (pauses new datapoint inflow)',
    },
    GET_DATASET_OPS: {
        name: 'Dataset Operation Status',
        description: 'Get status of dataset operations',
    },
    CREATE_EXPORT: {
        name: 'Create Dataset Export',
        description: 'Create a new export for the dataset',
    },
    START_DIRECT_UPLOAD_IMPORT: {
        name: 'Start direct upload import',
        description: 'Start a large dataset import via blob upload',
    },
    REPORT_DIRECT_UPLOAD_FILES: {
        name: 'Report direct upload files',
        description: 'Continue direct import after media files are in blob storage',
    },
    CREATE_TASK: {
        name: 'Create Task',
        description: 'Create a new task in the project',
    },
    EDIT_TASK: {
        name: 'Edit Task',
        description: 'Edit an existing task',
    },
    GET_TASKS: {
        name: 'Get Tasks',
        description: 'Get all tasks in the project',
    },
    GET_TASK_DRAFT: {
        name: 'Get Task Draft',
        description: 'Get the current user’s saved create-task draft for this project',
    },
    SAVE_TASK_DRAFT: {
        name: 'Save Task Draft',
        description: 'Autosave the create-task form as a DRAFT task row for this user and project',
    },
    GET_TASK: {
        name: 'Get Task',
        description: 'Get a specific task in the project',
    },
    QR_CODE_PAYLOAD: {
        name: 'QR Code Payload',
        description: 'Get QR code payload for a task',
    },
    MARK_TASK_COMPLETE: {
        name: 'Mark Task Complete',
        description: 'Mark a task as completed',
    },
    MOVE_TASK_TO_LIVE: {
        name: 'Move Task to Live',
        description: 'End preview dry-run: purge preview artifacts and promote the task chain to LIVE',
    },
    GET_TASK_ASSIGNMENTS: {
        name: 'Get Task Assignments',
        description: 'Get all task assignments for the project',
    },
    CREATE_TASK_ASSIGNMENT: {
        name: 'Create Task Assignment',
        description: 'Create a new task assignment for a task',
    },
    GET_AVATAR_SUMMARY: {
        name: 'Get Avatar Summary',
        description: 'Get summary of avatars for the project',
    },
    GENERATE_AVATARS: {
        name: 'Generate Avatars',
        description: 'Bulk create new avatars for the specified tasks',
    },
    LINK_AVATARS_BY_PHONE: {
        name: 'Link Avatars',
        description: 'Create and link avatars for a task by using phone numbers',
    },
    GET_AUTO_LINK_AVATARS: {
        name: 'Get Automatically Linked Avatars',
        description: 'Get all the avatars that were automatically created and linked to a task',
    },
    LINK_AVATARS_WITH_WORKER_ID: {
        name: 'Link Avatars with Worker IDs',
        description: 'Create and link avatars for a task using worker IDs',
    },
    GET_AUTO_LINK_AVATARS_WITH_WORKER_ID: {
        name: 'Get Avatars Automatically Linked with Worker IDs',
        description: 'Get all the avatars that were automatically created and linked to a task using worker IDs',
    },
    GET_AVATAR_GEN_BULKS: {
        name: 'Get Avatar Generation Bulks',
        description: 'Get details of all the bulk avatar generations for a project',
    },
    GET_AVATARS_FOR_BULK: {
        name: 'Get Avatars For Bulk',
        description: 'Get details of all the avatars generated in a single bulk avatar generation',
    },
    GET_WORKER_SUMMARY_FOR_TASK: {
        name: 'Get Worker Summary For Task',
        description: 'Get summary of workers for a specific task',
    },
    SET_AVATAR_STATUS: {
        name: 'Set Avatar Status',
        description: 'Enable or disable avatars',
    },
    GET_AVATAR_WORKER_SUMMARY_FOR_TASK: {
        name: 'Get Avatar Summary For Task',
        description: 'Get summary of avatars for a specific task',
    },
    GET_ELIGIBLE_WORKERS_FOR_PAYMENT: {
        name: 'Get Eligible Workers For Payment',
        description: 'Get all the workers who are eligible for payment',
    },
    INITIATE_BULK_TRANSACTION: {
        name: 'Initiate Bulk Transaction',
        description: 'Initiate a bulk transaction for the eligible workers',
    },
    GET_ALL_BULK_TRANSACTIONS: {
        name: 'Get All Bulk Transactions',
        description: 'Get all the bulk transactions for the project',
    },
    GET_BULK_TRANSACTION_SUMMARIES: {
        name: 'Get Bulk Transaction Summaries',
        description: 'Get aggregated transaction summaries for all bulk transactions in the project',
    },
    GET_ALL_TRANSACTIONS: {
        name: 'Get All Transactions',
        description: 'Get all the transactions for the project',
    },
    GET_ALL_ACCOUNTS: {
        name: 'Get All Accounts',
        description: 'Get all the accounts for the project',
    },
    GET_REPORT_RESULT_DOWNLOAD_URL: {
        name: 'Get Report Result Download URL',
        description: 'Get the CSV SAS URL for the report result',
    },
    CREATE_REPORT: {
        name: 'Create Report',
        description: 'Create a new report',
    },
    UPDATE_REPORT: {
        name: 'Update Report',
        description: 'Update an existing report',
    },
    DELETE_REPORT: {
        name: 'Delete Report',
        description: 'Delete a report',
    },
    GET_SCRIPTS: {
        name: 'Get all available scripts',
        description: 'Get all the available scripts the are accessible by the user',
    },
    EXECUTE_SCRIPT: {
        name: 'Execute script',
        description: 'Execute a script with the provided parameters',
    },
    GET_ALL_SCRIPT_RUNS: {
        name: 'Get all script runs',
        description: 'Get all the script runs for the user',
    },
    GET_SCRIPT_RUN_DETAILS: {
        name: 'Get script run details',
        description: 'Get the details of a specific script run',
    },
    EDIT_TASK_ASSIGNMENT: {
        name: 'Edit Task Assignment',
        description: 'Edit an existing task assignment for a task',
    },
    GET_ASSIGNABLE_MICROTASK_COUNT: {
        name: 'Get Assignable Microtask Count',
        description: 'Get the count of assignable microtasks for a task',
    },
    UPDATE_PROJECT_PREBATCHING: {
        name: 'Update Project Prebatching',
        description: 'Enable or disable prebatched assignments (and the drain mode) for all tasks in the project',
    },
    MARK_PROJECT_COMPLETED: {
        name: 'Mark Project Completed',
        description: 'Mark an ACTIVE or INACTIVE project as COMPLETED (PROJECT_MANAGER only)',
    },
    MARK_PROJECT_INACTIVE: {
        name: 'Mark Project Inactive',
        description: 'Pause an ACTIVE project as INACTIVE (PROJECT_MANAGER only)',
    },
    MARK_PROJECT_ACTIVE: {
        name: 'Mark Project Active',
        description: 'Resume an INACTIVE or COMPLETED project to ACTIVE (PROJECT_MANAGER only)',
    },
    UPDATE_TASK_PREBATCHING: {
        name: 'Update Task Prebatching',
        description: 'Override prebatching and drain flags for a specific task',
    },
    GET_TASK_PREBATCH_STATUS: {
        name: 'Get Task Prebatch Status',
        description: 'Get the resolved prebatching flags and live eligibility for a task',
    },
    GET_TASK_HEALTH: {
        name: 'Get Task Health',
        description: 'Get operational health issues for a task',
    },
    GET_WORKER_ACCOUNTS: {
        name: 'Get Worker Accounts',
        description: 'Get all the accounts associated with a worker',
    },
    GET_REPORT_BY_ID_MOBILE: {
        name: 'Get Report By Id (Mobile)',
        description: 'Get a specific report by id for mobile',
    },
    GET_REPORT_RESULT_MOBILE: {
        name: 'Get Report Result (Mobile)',
        description: 'Get report result for mobile',
    },
    GET_REPORT_RESULT_DOWNLOAD_URL_MOBILE: {
        name: 'Get Report Result Download URL (Mobile)',
        description: 'Get the download URL for the report result for mobile',
    },
    LINK_AVATAR: {
        name: 'Link Avatar',
        description: 'Link an avatar to a worker',
    },
    GET_WORKER_MASKED_PII: {
        name: 'Get Worker Masked PII',
        description: 'Get masked PII data for all workers in the project',
    },
    REVEAL_WORKER_PII_FIELD: {
        name: 'Reveal Worker PII Field',
        description: 'Reveal a single PII field for a worker in the project',
    },
    UPDATE_LANGUAGE_ASSET: {
        name: 'Update Language Asset',
        description: 'Update language asset',
    },
    GET_CONFERENCER_SUMMARY: {
        name: 'Get Conferencer Summary',
        description: 'Get aggregated conferencer dashboard summary after a date',
    },
    GET_CONFERENCER_RECORDS: {
        name: 'Get Conferencer Records',
        description: 'Get conferencer records',
    },
    GET_CONFERENCER_RECORDS_SUMMARY: {
        name: 'Get Conferencer Records Summary',
        description: 'Get aggregated summary for selected conferencer records',
    },
    GET_WORKER_TASK_LEDGER: {
        name: 'Get Worker Task Ledger',
        description: 'Get worker-task level payment ledger for a project',
    },
    GET_WORKER_PAYMENT_LEDGER: {
        name: 'Get Worker Payment Ledger',
        description: 'Get worker-level payment ledger for a project',
    },
    GET_TROUBLESHOOTING_WORKER_LOOKUP: {
        name: 'Lookup Worker for Troubleshooting',
        description: 'Resolve a worker by ID, phone, or access code and return troubleshooting view',
    },
    GET_TROUBLESHOOTING_WORKER: {
        name: 'Get Worker Troubleshooting View',
        description: 'Get linked avatars and payment summary for a worker in the acting project',
    },
    GET_ADDITIONAL_PAYMENT_LEDGER: {
        name: 'Get Additional Payment Ledger',
        description: 'Get per-worker additional payment ledger for a TASK-mode project',
    },
    GET_KYC_VERIFIED_WORKERS: {
        name: 'Get KYC Verified Workers',
        description: 'List workers in your org who have completed PAN KYC. Does not expose document numbers.',
    },
    DOWNLOAD_KYC_PAN_REPORT: {
        name: 'Download KYC PAN Report',
        description: 'Export a report containing decrypted PAN numbers for KYC verified workers. Every export is logged.',
    },
    GET_TASK_PAYMENT_ROWS: {
        name: 'Get Task Payment Rows',
        description: 'Get payable worker×task rows for task-level bulk payments',
    },
};
//# sourceMappingURL=Index.js.map